import { toast } from '@/hooks/use-toast';
import { websocketService } from '@/lib/websocket';
import { SocketMessageData } from '@/lib/types/socket';

// Convert string literal types to enums for better TypeScript inference
export enum NotificationType {
  MESSAGE = 'message',
  TASK_ASSIGNED = 'task_assigned',
  TASK_COMPLETED = 'task_completed',
  TASK_STATUS_CHANGED = 'task_status_changed',
  CONTRACT_UPDATED = 'contract_updated',
  CONTRACT_ACTIVATED = 'contract_activated',
  CONTRACT_CANCELLED = 'contract_cancelled',
  CONTRACT_RENEWED = 'contract_renewed',
  CONTRACT_EXPIRED = 'contract_expired',
  PAYMENT_PROCESSED = 'payment_processed',
  PAYMENT_FAILED = 'payment_failed',
  RENEWAL_FAILED = 'renewal_failed',
  MILESTONE_PROGRESS_UPDATED = 'milestone_progress_updated',
  MILESTONE_OVER_BUDGET = 'milestone_over_budget',
  MAINTENANCE_ALERT = 'maintenance_alert',
  SYSTEM_ALERT = 'system_alert',
  CUSTOM_TERMS_APPROVED = 'custom_terms_approved'
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high'
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  timestamp: string;
  read: boolean;
  data?: Record<string, unknown>;
  recipientId: string;
}

export interface NotificationPreferences {
  userId: string;
  email: boolean;
  push: boolean;
  inApp: boolean;
  categories: Record<NotificationType, {
    email: boolean;
    push: boolean;
    inApp: boolean;
  }>;
}

interface NotificationPayload {
  notification: Notification;
}

// Define a type for toast variants
type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

class NotificationService {
  private static instance: NotificationService;
  private preferences: Map<string, NotificationPreferences> = new Map();

  private constructor() {
    this.initializeWebSocket();
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private initializeWebSocket() {
    websocketService.onMessage((data: SocketMessageData) => {
      if (data.type === 'notification') {
        this.handleNotification((data as NotificationPayload).notification);
      }
    });
  }

  private handleNotification(notification: Notification) {
    // Show in-app toast notification
    toast({
      title: notification.title,
      message: notification.message,
      variant: this.getPriorityVariant(notification.priority),
    });

    // Trigger browser notification if permitted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/notification-icon.png',
      });
    }

    // Emit event for real-time updates
    this.emit('notification', notification);
  }

  private getPriorityVariant(priority: NotificationPriority): ToastVariant {
    switch (priority) {
      case NotificationPriority.HIGH:
        return 'destructive';
      case NotificationPriority.MEDIUM:
        return 'warning';
      case NotificationPriority.LOW:
      default:
        return 'default';
    }
  }

  private emit(event: string, data: unknown) {
    const customEvent = new CustomEvent(event, { detail: data });
    window.dispatchEvent(customEvent);
  }

  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  async updatePreferences(userId: string, preferences: Partial<NotificationPreferences>) {
    try {
      // Here you would make an API call to update preferences
      const currentPrefs = this.preferences.get(userId) || this.getDefaultPreferences(userId);
      const updatedPrefs = { ...currentPrefs, ...preferences };
      this.preferences.set(userId, updatedPrefs);

      websocketService.sendMessage({
        type: 'update_notification_preferences',
        userId,
        preferences: updatedPrefs,
      });

      return updatedPrefs;
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  }

  getPreferences(userId: string): NotificationPreferences {
    return (
      this.preferences.get(userId) ||
      this.getDefaultPreferences(userId)
    );
  }

  private getDefaultPreferences(userId: string): NotificationPreferences {
    return {
      userId,
      email: true,
      push: true,
      inApp: true,
      categories: {
        [NotificationType.MESSAGE]: { email: true, push: true, inApp: true },
        [NotificationType.TASK_ASSIGNED]: { email: true, push: true, inApp: true },
        [NotificationType.TASK_COMPLETED]: { email: true, push: true, inApp: true },
        [NotificationType.TASK_STATUS_CHANGED]: { email: true, push: true, inApp: true },
        [NotificationType.CONTRACT_UPDATED]: { email: true, push: true, inApp: true },
        [NotificationType.CONTRACT_ACTIVATED]: { email: true, push: true, inApp: true },
        [NotificationType.CONTRACT_CANCELLED]: { email: true, push: true, inApp: true },
        [NotificationType.CONTRACT_RENEWED]: { email: true, push: true, inApp: true },
        [NotificationType.CONTRACT_EXPIRED]: { email: true, push: true, inApp: true },
        [NotificationType.PAYMENT_PROCESSED]: { email: true, push: true, inApp: true },
        [NotificationType.PAYMENT_FAILED]: { email: true, push: true, inApp: true },
        [NotificationType.RENEWAL_FAILED]: { email: true, push: true, inApp: true },
        [NotificationType.MILESTONE_PROGRESS_UPDATED]: { email: true, push: true, inApp: true },
        [NotificationType.MILESTONE_OVER_BUDGET]: { email: true, push: true, inApp: true },
        [NotificationType.MAINTENANCE_ALERT]: { email: true, push: true, inApp: true },
        [NotificationType.SYSTEM_ALERT]: { email: true, push: true, inApp: true },
        [NotificationType.CUSTOM_TERMS_APPROVED]: { email: true, push: true, inApp: true },
      },
    };
  }
}

export const notificationService = NotificationService.getInstance();

// Export a standalone sendNotification function for API handlers to use
export interface SendNotificationOptions {
  recipientId: string;
  type: NotificationType;
  title: string;
  message: string;
  priority?: NotificationPriority;
  data?: Record<string, unknown>;
}

export async function sendNotification(options: SendNotificationOptions): Promise<void> {
  const { recipientId, type, title, message, priority = NotificationPriority.MEDIUM, data } = options;
  
  // Create a notification object
  const notification: Notification = {
    id: crypto.randomUUID(),
    type,
    title,
    message,
    priority,
    timestamp: new Date().toISOString(),
    read: false,
    recipientId,
    data
  };
  
  try {
    // In a real implementation, this would store the notification in the database
    console.log(`Sending notification to ${recipientId}:`, notification);
    
    // Use the websocket service to deliver in-app notification
    websocketService.sendMessage({
      type: 'notification',
      notification
    });
    
    // Return immediately as this is an async operation
    return Promise.resolve();
  } catch (error) {
    console.error('Failed to send notification:', error);
    return Promise.reject(error);
  }
}